/**
 * =============================================================================
 * WlAddonsListCard
 *
 * Card com a listagem de add-ons contratados pelos tenants seguindo o mesmo
 * padrão visual do `WlSubscriptionsListCard` (aba Assinaturas):
 *   - Header com título, subtítulo (contador) e pills de status à direita
 *   - Busca inline por tenant / add-on (debounce simples via onBlur/Enter)
 *   - Tabela com header sticky, scroll vertical dentro do card
 *   - Linha clicável abrindo o modal de detalhes do tenant (drill-down)
 *   - Coluna "Dt. criação" (data/hora) — novo requisito
 *   - Paginação server-side via PaginationFooter (15/30/50)
 *
 * Consome a RPC `wl_list_tenant_addons` v2.0.0 (parâmetros aditivos).
 *
 * @version 1.0.0
 * RELEASE: 2026-07-22 v1.0.0 - Versão inicial (substitui a listagem legada
 *          embutida em WlFinancial.tsx).
 * =============================================================================
 */
import { useEffect, useMemo, useState } from 'react';
import { Package, Search } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { WlTenantDetailsModal } from '@/components/wl/WlTenantDetailsModal';
import { cn } from '@/lib/utils';

// ----- Tipos -----------------------------------------------------------------
interface AddonRow {
  id: string;
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  addon_id: string;
  addon_name: string;
  addon_slug: string;
  addon_type: string;
  quantity: number;
  status: string;
  price_monthly: number | null;
  setup_fee_paid: boolean | null;
  activated_at: string | null;
  cancelled_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  /** v2.0.0 da RPC — usada na coluna "Dt. criação". */
  created_at: string | null;
  total_count: number;
}

// ----- Helpers ---------------------------------------------------------------
const fmtBRL = (v: number | null | undefined) =>
  v == null
    ? '—'
    : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
        Number(v),
      );

const fmtDate = (v: string | null) =>
  v ? new Date(v).toLocaleDateString('pt-BR') : '—';

/** Data + hora curta em pt-BR (ex.: "22/07/2026 14:32"). */
const fmtDateTime = (v: string | null) => {
  if (!v) return '—';
  const d = new Date(v);
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

/** Cores por status de add-on (padrão da aba Assinaturas). */
const statusBadgeClass: Record<string, string> = {
  active: 'border-green-500/40 text-green-600 dark:text-green-400',
  cancelled: 'border-red-500/40 text-red-600 dark:text-red-400',
  canceled: 'border-red-500/40 text-red-600 dark:text-red-400',
  pending: 'border-yellow-500/40 text-yellow-600 dark:text-yellow-400',
  expired: 'border-muted-foreground/30 text-muted-foreground',
};

// Filtros locais (pills) — mapeiam para o param p_status da RPC.
type LocalFilter = null | 'active' | 'cancelled' | 'pending' | 'expired';

interface Props {
  /** Mês de referência vindo do header global. Ainda não filtra a listagem
   *  por padrão (add-ons são um snapshot); reservado para futuras extensões. */
  referenceMonth?: string | null;
  className?: string;
}

export function WlAddonsListCard({ className }: Props) {
  const { toast } = useToast();

  // Paginação e filtros locais.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [statusFilter, setStatusFilter] = useState<LocalFilter>(null);
  const [search, setSearch] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');

  const [rows, setRows] = useState<AddonRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Drill-down por linha (mesmo modal usado na aba Assinaturas).
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.rpc('wl_list_tenant_addons', {
          p_tenant_id: null,
          p_status: statusFilter,
          p_limit: pageSize,
          p_offset: (page - 1) * pageSize,
          p_addon_id: null,
          p_include_courtesy: false,
          p_search: committedSearch.trim() || null,
        } as never);
        if (error) throw error;
        if (cancelled) return;
        const list = (data ?? []) as AddonRow[];
        setRows(list);
        setTotal(list[0]?.total_count ?? 0);
      } catch (err: any) {
        if (!cancelled) {
          toast({
            title: 'Erro ao carregar add-ons',
            description: err.message,
            variant: 'destructive',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, statusFilter, committedSearch, toast]);

  const PillBtn = useMemo(
    () =>
      function PillBtn({ value, label }: { value: LocalFilter; label: string }) {
        const active = statusFilter === value;
        return (
          <Button
            size="sm"
            variant={active ? 'default' : 'outline'}
            className="h-7 px-2.5 text-xs"
            onClick={() => {
              setStatusFilter(value);
              setPage(1);
            }}
          >
            {label}
          </Button>
        );
      },
    [statusFilter],
  );

  return (
    <Card className={cn('flex flex-col', className)}>
      <CardHeader className="flex flex-col gap-3 pb-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-0.5 min-w-0">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Package className="h-4 w-4 text-muted-foreground" />
            Add-ons
          </CardTitle>
          <span className="text-[11px] text-muted-foreground">
            Add-ons contratados pelos tenants — {total} registro{total === 1 ? '' : 's'}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Buscar tenant ou add-on…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setCommittedSearch(search);
                  setPage(1);
                }
              }}
              onBlur={() => {
                if (search !== committedSearch) {
                  setCommittedSearch(search);
                  setPage(1);
                }
              }}
              className="h-7 pl-7 pr-2 text-xs w-[200px]"
            />
          </div>
          <PillBtn value={null} label="Todos" />
          <PillBtn value="active" label="Ativos" />
          <PillBtn value="pending" label="Pendentes" />
          <PillBtn value="cancelled" label="Cancelados" />
          <PillBtn value="expired" label="Expirados" />
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div className="max-h-[520px] overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead className="text-xs font-medium">Tenant</TableHead>
                <TableHead className="text-xs font-medium">Add-on</TableHead>
                <TableHead className="text-xs font-medium">Tipo</TableHead>
                <TableHead className="text-xs font-medium text-right">Qtd</TableHead>
                <TableHead className="text-xs font-medium text-right">Valor unit/mês</TableHead>
                <TableHead className="text-xs font-medium">Status</TableHead>
                <TableHead className="text-xs font-medium">Dt. criação</TableHead>
                <TableHead className="text-xs font-medium">Vigência</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center py-6 text-sm text-muted-foreground"
                  >
                    Carregando…
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center py-6 text-sm text-muted-foreground"
                  >
                    Nenhum add-on encontrado
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((a) => {
                  const badgeCls =
                    statusBadgeClass[a.status] ??
                    'border-muted-foreground/30 text-muted-foreground';
                  return (
                    <TableRow
                      key={a.id}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => setSelectedTenantId(a.tenant_id)}
                      title="Abrir detalhes do tenant"
                    >
                      <TableCell className="py-2 text-xs font-medium">
                        <div className="flex flex-col">
                          <span className="truncate">{a.tenant_name}</span>
                          <span className="text-[10px] text-muted-foreground">
                            {a.tenant_slug}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="py-2 text-xs">
                        <div className="flex flex-col">
                          <span className="truncate">{a.addon_name}</span>
                          <span className="text-[10px] text-muted-foreground">
                            {a.addon_slug}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="py-2 text-xs">
                        <Badge variant="outline" className="text-[10px]">
                          {a.addon_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-2 text-xs text-right">
                        {a.quantity}
                      </TableCell>
                      <TableCell className="py-2 text-xs text-right font-medium">
                        {fmtBRL(a.price_monthly)}
                      </TableCell>
                      <TableCell className="py-2">
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-[10px] font-medium whitespace-nowrap',
                            badgeCls,
                          )}
                        >
                          {a.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-2 text-[11px] whitespace-nowrap">
                        {fmtDateTime(a.created_at)}
                      </TableCell>
                      <TableCell className="py-2 text-[11px] whitespace-nowrap">
                        {fmtDate(a.current_period_start)} → {fmtDate(a.current_period_end)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {total > pageSize && (
          <PaginationFooter
            totalItems={total}
            currentPage={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(sz) => {
              setPageSize(sz);
              setPage(1);
            }}
            pageSizeOptions={[15, 30, 50]}
          />
        )}
      </CardContent>

      {/* Drill-down: detalhes do tenant selecionado. */}
      <WlTenantDetailsModal
        tenantId={selectedTenantId}
        open={!!selectedTenantId}
        onClose={() => setSelectedTenantId(null)}
      />
    </Card>
  );
}
